import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { _t } from "../../../../../functions/Functions";

/**
 * Returns available units for an item:
 * 1. default_unit (id: default_unit_id, name: default_unit_name)
 * 2. unit_name (id: unit_id, name: unit_name)
 * Gracefully falls back to item.unit or item.unit_code if neither is available.
 */
export const getItemUnits = (item) => {
  if (!item) return [];
  const units = [];

  if (item.default_unit_name) {
    units.push({
      id: item.default_unit_id,
      name: item.default_unit_name,
      code: item.default_unit_code || item.default_unit_name,
      label: item.default_unit_name,
      value: item.default_unit_name,
    });
  }

  if (item.unit_name && item.unit_name !== item.default_unit_name) {
    units.push({
      id: item.unit_id,
      name: item.unit_name,
      code: item.unit || item.unit_code || item.unit_name,
      label: item.unit_name,
      value: item.unit_name,
    });
  }

  if (units.length === 0 && (item.unit || item.unit_code)) {
    const fallback = item.unit || item.unit_code;
    units.push({
      id: item.unit_id || null,
      name: fallback,
      code: item.unit_code || fallback,
      label: fallback,
      value: fallback,
    });
  }

  return units;
};

export const getDefaultUnit = (item) => {
  return item?.default_unit_name || item?.unit_name || item?.unit || item?.unit_code || "";
};

const preventInvalidNumKeys = (e) => {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};

const ProductionAddStockModal = ({
  show,
  onClose,
  onAdd,
  items = [],
  existingInItems = [],
  roundOffStock = (val) => val,
  qtyAmountPoints = () => "any",
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState({});

  // Sync state on modal open
  useEffect(() => {
    if (show) {
      const initial = {};
      if (existingInItems?.length) {
        existingInItems.forEach((it) => {
          const id = it.ingredient_id || it.id;
          initial[id] = {
            qty: it.actual_qty ?? it.ingredient_qty ?? it.qty ?? 1,
            cost_price: it.cost_price ?? 0,
            unit: it.unit_name || it.unit_code || it.unit || getDefaultUnit(it),
            selected: true,
          };
        });
      }
      setSelectedData(initial);
      setSearchTerm("");
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  }, [show, existingInItems]);

  // Handle ESC key to close
  useEffect(() => {
    if (!show) return;
    const handleEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [show, onClose]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!items?.length) return [];
    if (!searchTerm) return items;
    const lower = searchTerm.toLowerCase().trim();
    return items.filter((it) => {
      const name = String(it.name || it.ingredient_name || "").toLowerCase();
      const id = String(it.id || "").toLowerCase();
      const code = String(it.code || "").toLowerCase();
      return name.includes(lower) || id.includes(lower) || code.includes(lower);
    });
  }, [items, searchTerm]);

  const displayedItems = useMemo(() => filteredItems.slice(0, 300), [filteredItems]);

  const handleToggleSelect = useCallback((item) => {
    setSelectedData((prev) => {
      const curr = prev[item.id] || {
        qty: 1,
        cost_price: item.cost_price ?? 0,
        unit: getDefaultUnit(item),
      };
      return { ...prev, [item.id]: { ...curr, selected: !curr.selected } };
    });
  }, []);

  const handleFieldChange = useCallback((item, field, value) => {
    setSelectedData((prev) => {
      const curr = prev[item.id] || {
        qty: 1,
        cost_price: item.cost_price ?? 0,
        unit: getDefaultUnit(item),
      };
      return { ...prev, [item.id]: { ...curr, [field]: value, selected: true } };
    });
  }, []);

  const isAllFilteredSelected = useMemo(() => {
    return (
      filteredItems.length > 0 &&
      filteredItems.every((item) => selectedData[item.id]?.selected)
    );
  }, [filteredItems, selectedData]);

  const toggleSelectAll = () => {
    setSelectedData((prev) => {
      const updated = { ...prev };
      const newStatus = !isAllFilteredSelected;
      filteredItems.forEach((it) => {
        const curr = updated[it.id] || {
          qty: 1,
          cost_price: it.cost_price ?? 0,
          unit: getDefaultUnit(it),
        };
        updated[it.id] = { ...curr, selected: newStatus };
      });
      return updated;
    });
  };

  const selectedCount = useMemo(() => {
    return Object.values(selectedData).filter((d) => d?.selected).length;
  }, [selectedData]);

  const handleAddRecords = () => {
    const itemsToAdd = [];
    Object.keys(selectedData).forEach((id) => {
      if (selectedData[id]?.selected) {
        const item = items.find((it) => String(it.id) === String(id));
        if (item) {
          const data = selectedData[id];
          const qty = data.qty !== "" ? parseFloat(data.qty) || 0 : 1;
          const cost_price = data.cost_price !== "" ? parseFloat(data.cost_price) || 0 : 0;
          const itemUnits = getItemUnits(item);
          const selectedUnit = itemUnits.find((u) => u.value === data.unit) || itemUnits[0] || {};

          itemsToAdd.push({
            unit_id: selectedUnit.id ?? item.unit_id ?? item.default_unit_id ?? null,
            unit_name: selectedUnit.name ?? item.unit_name ?? item.default_unit_name ?? "",
            unit_code: selectedUnit.code ?? item.unit_code ?? item.unit ?? "",
            ingredient_id: item.id,
            ingredient_name: item.name || item.ingredient_name || "",
            order_type: item.order_type || "Dine-In, Delivery, Takeaway,",
            ingredient_qty: qty,
            cost_price: cost_price,
            cost_amount: roundOffStock(qty * cost_price),
            actual_qty: qty,
            stock_qty: item.stock_qty || 0,
            stock_type: "IN",
          });
        }
      }
    });
    onAdd(itemsToAdd);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1050 }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center rounded-top">
            <h5 className="modal-title d-flex align-items-center mb-0 font-weight-bold text-dark">
              <i className="fa fa-cubes mr-2 text-primary"></i>
              {_t(t("Add Stock Items"))}
              <div className="d-flex align-items-center ml-3">
                <span className="badge font-weight-normal bg-light text-secondary border mr-2 py-1 px-2">
                  {items.length > 0 ? `${filteredItems.length} / ${items.length}` : 0} {_t(t("Results"))}
                </span>
                {selectedCount > 0 && (
                  <span className="badge font-weight-normal text-white bg-primary shadow-sm py-1 px-2">
                    {selectedCount} {_t(t("Selected"))}
                  </span>
                )}
              </div>
            </h5>
            <button
              type="button"
              className="close xslg-text shadow-none border-0 bg-transparent text-muted"
              onClick={onClose}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>

          <div className="modal-body p-0 bg-light">
            <div className="p-3 border-bottom bg-white">
              <div className="position-relative">
                <i
                  className="fa fa-search position-absolute text-secondary"
                  style={{ left: "14px", top: "50%", transform: "translateY(-50%)" }}
                ></i>
                <input
                  type="text"
                  className="form-control pl-5 shadow-sm border bg-light table-text rounded"
                  placeholder={_t(t("Search item by name, code or ID..."))}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  ref={searchInputRef}
                />
              </div>
            </div>

            <div className="custom-scrollbar px-2 pt-2 pb-3" style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <div className="bg-white rounded border overflow-hidden">
                <table className="table table-hover table-sm mb-0">
                  <thead className="bg-light sticky-top shadow-sm" style={{ zIndex: 1 }}>
                    <tr>
                      <th className="align-middle border-bottom-0 text-center" style={{ width: "50px" }}>
                        <div className="form-check cursor-pointer mt-0 custom-checkbox mb-0 d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isAllFilteredSelected}
                            onChange={toggleSelectAll}
                          />
                        </div>
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary" style={{ minWidth: "180px", letterSpacing: "0.5px" }}>
                        {_t(t("Name"))}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary text-center" style={{ width: "120px", letterSpacing: "0.5px" }}>
                        {_t(t("Qty"))}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary text-center" style={{ width: "130px", letterSpacing: "0.5px" }}>
                        {_t(t("Cost Price"))}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary text-center" style={{ width: "130px", letterSpacing: "0.5px" }}>
                        {_t(t("Cost Amount"))}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary text-center" style={{ width: "150px", letterSpacing: "0.5px" }}>
                        {_t(t("Unit"))}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedItems.length > 0 ? (
                      displayedItems.map((item) => {
                        const itemState = selectedData[item.id] || {
                          qty: 1,
                          cost_price: item.cost_price ?? 0,
                          unit: getDefaultUnit(item),
                          selected: false,
                        };
                        const isSelected = !!itemState.selected;
                        const qtyNum = itemState.qty !== "" ? parseFloat(itemState.qty) || 0 : 0;
                        const costNum = itemState.cost_price !== "" ? parseFloat(itemState.cost_price) || 0 : 0;
                        const amount = roundOffStock(qtyNum * costNum);
                        const itemUnits = getItemUnits(item);

                        return (
                          <tr
                            key={item.id}
                            style={{ backgroundColor: isSelected ? "rgba(0, 123, 255, 0.04)" : "transparent" }}
                          >
                            <td className="align-middle text-center">
                              <div className="custom-checkbox mb-0 d-flex justify-content-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer mt-0"
                                  checked={isSelected}
                                  onChange={() => handleToggleSelect(item)}
                                />
                              </div>
                            </td>
                            <td
                              className={`py-2 align-middle table-text cursor-pointer ${
                                isSelected ? "text-primary font-weight-bold" : "text-dark"
                              }`}
                              onClick={() => handleToggleSelect(item)}
                            >
                              {item.name || item.ingredient_name || "-"}
                            </td>
                            <td className="py-2 align-middle text-center">
                              <input
                                type="number"
                                step={qtyAmountPoints()}
                                min="0"
                                onKeyDown={preventInvalidNumKeys}
                                onWheel={(e) => e.target.blur()}
                                className="form-control form-control-sm text-center"
                                value={itemState.qty}
                                onChange={(e) => handleFieldChange(item, "qty", e.target.value)}
                                placeholder="Qty"
                              />
                            </td>
                            <td className="py-2 align-middle text-center">
                              <input
                                type="number"
                                step={qtyAmountPoints()}
                                min="0"
                                onKeyDown={preventInvalidNumKeys}
                                onWheel={(e) => e.target.blur()}
                                className="form-control form-control-sm text-center"
                                value={itemState.cost_price}
                                onChange={(e) => handleFieldChange(item, "cost_price", e.target.value)}
                                placeholder="Cost"
                              />
                            </td>
                            <td className="py-2 align-middle text-center">
                              <input
                                type="text"
                                className="form-control form-control-sm text-center bg-light"
                                value={amount}
                                readOnly
                              />
                            </td>
                            <td className="py-2 align-middle text-center">
                              <select
                                className="form-control form-control-sm text-center"
                                value={itemState.unit}
                                onChange={(e) => handleFieldChange(item, "unit", e.target.value)}
                              >
                                {itemUnits.map((u, i) => (
                                  <option key={i} value={u.value}>
                                    {u.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-5 text-muted border-0">
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-folder-open-o mb-3 text-secondary" style={{ fontSize: "2.5rem" }}></i>
                            <span className="table-text font-weight-bold">{_t(t("No Item Found"))}</span>
                            {searchTerm && (
                              <small className="mt-1 text-secondary">
                                {_t(t("Try adjusting your search criteria"))}
                              </small>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer py-2 px-4 bg-white d-flex justify-content-between align-items-center border-top rounded-bottom">
            <small className="text-muted d-flex align-items-center sm-text">
              <kbd className="mr-2 shadow-sm bg-light text-secondary border font-weight-bold">ESC</kbd>{" "}
              {_t(t("to close"))}
            </small>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary px-4 py-2 table-text font-weight-bold rounded mr-2"
                onClick={onClose}
              >
                {_t(t("Cancel"))}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary px-4 py-2 d-flex align-items-center table-text font-weight-bold rounded"
                disabled={selectedCount === 0}
                onClick={handleAddRecords}
              >
                <i className="fa fa-plus mr-2"></i>
                {_t(t("Add Selected Items"))}
                {selectedCount > 0 && (
                  <span className="badge bg-white text-primary ml-2">{selectedCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionAddStockModal;
